import React, { useEffect, useState, useMemo } from "react";
import {
  Button,
  Card,
  CardBody,
  CardHeader,
  Col,
  Container,
  Input,
  Row,
  Spinner,
  Nav,
  NavItem,
  NavLink,
  TabContent,
  TabPane,
  Modal,
  ModalBody
} from "reactstrap";
import BreadCrumb from "../../../Components/Common/BreadCrumb";
import { useTranslation } from "react-i18next";
import { getAuthUser } from "../../../helpers/utils";
import { useDispatch, useSelector } from "react-redux";
import DeleteModal from "../../../Components/Common/DeleteModal";
import ColumnSelectionModal from "../../../Components/Common/ColumnSelectionModal/ColumnSelectionModal";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "react-toastify";
import CommonDataTable from "../../../Components/Common/Table/CommonDataTable";
import usePermissions from "../../../Components/Hooks/usePermissions";
import { fetchAllCases, fetchCaseTableColumnNames, updateCase, getCaseJson, deleteCases, updateCaseJson } from "../../../slices/cases/thunk";
import FileCommentModal from "../../../Components/Common/Modal/FileCommentModal";
import FileAssignModal from "../../../Components/Common/Modal/FileAssignModal";
import MultiFileModal from "../../../Components/Common/Modal/MultiFileModal";
import SaveFileModal from "../../../Components/Common/Modal/SaveFileModal";
import { getCases, updateCaseProcessingStatus } from "../../../slices/cases/reducer";
import { updateUserPreference} from "../../../slices/thunks"; 
import { useSocket } from "../../../hooks/useSocket";
import { fetchUsers } from "../../../slices/thunks";
import Cookies from 'js-cookie';

const tabObj = {
  1: 'bg-process',  
  4: 'current',
  98: 'data-entry',
  118: 'under-review'
}

function Cases () {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { user } = getAuthUser();
  const { socket, isConnected } = useSocket();
  const [searchParams, setSearchParams] = useSearchParams();

  const [searchTerm, setSearchTerm] = useState("");
  const [selectedRow, setSelectedRow] = useState({});
  const [selectedIds, setSelectedIds] = useState([]);
   const [modalOpen, setModalOpen] = useState(false);  
  const [deleteModal, setDeleteModal] = useState(false); 
  const [commentModal, setCommentModal] = useState(false);  
  const [selectedColumns, setSelectedColumns] = useState([]);
  const [showMultiLaw, setShowMultiLaw] = useState(false); 
  const [messages, setMessages] = useState([]);
  
  const [showConfirm, setShowConfirm] = useState(false);
  const [showStatusConfirm, setShowStatusConfirm] = useState(false);
  const [activeTab, setActiveTab] = useState(searchParams.get("status") ? searchParams.get("status") : tabObj[user.RoleID] || "current");

  const { 
    cases, 
    loading,
    headerLoading,
    tableColumnNames, 
   } = useSelector(
    (state) => ({
      cases: state.Cases.cases,
      loading: state.Cases.loading,
      headerLoading: state.Cases.headerLoading,
      tableColumnNames: state.Cases.tableColumnNames, 
    })
  );

  // Compute permission
  const permission = (() => {
    return usePermissions(2, 14);
  })();

   const handleTabChange = (tab) => {
    const newParams = {};
    if (tab) newParams.status = tab;

    setSearchParams(newParams);
    setActiveTab(tab);
  }

  const handleViewClick = (item) => {
    if (item && item.FileURL) {
      window.open(item.FileURL, '_blank', 'noopener,noreferrer');
    } else {
      toast.error(t("FileURLNotAvailable", "File URL is not available for this document."));
    }
  };
  
  const handleCommentClick = (item) => {
    setSelectedRow(item);
    setCommentModal(true);  
  }
  
  const handleRetry = (item) => {
    try {
      dispatch(updateCaseProcessingStatus({ ID: item.ID, Status: 10, JsonURL: null, ModifiedByID: user.ID }));

      let formData = {
        UserID: user.ID,
        DocID: item.ID,
        DocURL: item.FileURL,
        JsonURL: item.JsonURL,
        StatusID: [12, 13].includes(item.Status) ? item.Status : 5,
        IsRetry: true
      }

      formData = JSON.stringify(formData);

      dispatch(getCaseJson({ formData, onProgress, isMultiUpload: false }));
    } catch (error) {
      console.error("Error in handleButtonClick:", error);
    }
  }
  
  const handleReassign = (item) => {  
    setSelectedRow(item);
    setShowConfirm(true);
  }

  const onReassignClickHandler = (paraLegalIDs) => {
    const data = {
      CaseID: selectedRow.ID,
      JsonURL: selectedRow.JsonURL,
      Status: 5,
      ParaLegalIDs: paraLegalIDs,
    }
    
    dispatch(updateCaseJson(data))
    setShowConfirm(false);
  }
  
  const handleSaveCase = (item) => {
    setSelectedRow(item);
    setShowStatusConfirm(true);
  }
  
  const onSaveClickHandler = () => {
    dispatch(updateCaseJson({ 
      CaseID: selectedRow.ID, 
      // JsonURL: selectedRow.JsonURL,
      Status: 5,
      ParaLegalIDs: selectedRow.ParaLegalIDs,
    }))
    setSelectedRow({});
    setShowStatusConfirm(false);
  }

  const handleCommentSubmit = (value) => {
    // e.preventDefault();
    const data = {
      CaseID: selectedRow.ID, 
      Comment: value.comment, 
      Status: value.reason
    }

    dispatch(updateCase(data));
    setCommentModal(false);
  }

  const handleDeleteActionClick = (item) => {
    setSelectedIds([item.ID || item.Id]);
    setDeleteModal(true);
  };

  const onProgress = ({ event, data }) => {
    try {
      const parsedData = typeof data === "string" ? JSON.parse(data) : data;

      if (event === "multiple_laws_detected") {
        setShowMultiLaw(true);
        setMessages(parsedData?.titles || [])
      }

      if (event === "new_json") {
        const { DocID, JsonUrl = null, StatusID } = parsedData;
        dispatch(updateCaseProcessingStatus({ ID: DocID, Status: StatusID, JsonURL: JsonUrl, ModifiedByID: user.ID }))
      }
      
    } catch (error) {
      console.error("Failed to process stream event:", error, data);
    }
  };

  const handleButtonClick = async (item, title, type) => {
    if (type == "view") {
      navigate('/ai/case-details', { 
        state:  {
          DocID: item.ID,
          FileName: item.FileName,
          FileURL: item.FileURL,
          JsonURL: item.JsonURL,
          Status: item.Status,
          ReviewedBy: item.ReviewedBy,
          ParaLegalIDs: item.ParaLegalIDs,
        }
      })
    } else if (type == 'json-editor') {
      navigate('/ai/case-json-editor', { 
        state:  {
          DocID: item.ID,
          FileName: item.FileName,
          FileURL: item.FileURL,
          JsonURL: item.JsonURL,
          Status: item.Status,
          ReviewedBy: item.ReviewedBy,
          ParaLegalIDs: item.ParaLegalIDs,
        }
      })
    } else if (type == "view-metadata") {
      navigate('/ai/case-chunks', { 
        state:  {
          DocID: item.ID,
          FileName: item.FileName,
          FileURL: item.FileURL,
          JsonURL: item.JsonURL,
          Status: item.Status,
          ReviewedBy: item.ReviewedBy,
          ParaLegalIDs: item.ParaLegalIDs,
        }
      })

    } else if (type == 'get-json') {
      try {
        dispatch(updateCaseProcessingStatus({ ID: item.ID, Status: 3, JsonURL: null, ModifiedByID: user.ID}))

        let formData = {
          UserID: user.ID,
          DocID: item.ID,
          DocURL: item.FileURL,
          JsonURL: item.JsonURL,
          StatusID: 5,
          IsRetry: false
        }

        formData = JSON.stringify(formData);

        dispatch(getCaseJson({ formData, onProgress, isMultiUpload: false }));
      } catch (error) {
        console.error("Error in handleButtonClick:", error);
      }
    } else if (type == 'retry') {
      handleRetry(item);
    }
  }

  const handlePageChange = (page, record) => {
    dispatch(fetchAllCases({ PageNumber: page, ItemsPerPage: record }));
    // You can add any additional logic here if needed
  }

  const actionConfig = {
    view: { 
      icon: "ri-eye-line",
      tooltip: t("View"),
      onClick: handleViewClick,
      isVisible: (item, perm) => perm?.CanView, // Show for all items, or add permission check: perm?.CanView
    },
    retry: { 
      icon: "ri-restart-line",
      tooltip: t("Retry"),
      onClick: handleRetry,
      isVisible: (item, perm) =>  ![3, 10].includes(item.Status) && [1, 4, 118].includes(user.RoleID) && perm?.CanActive  ? true : false,
    },
    comment: { 
      icon: "ri-file-close-line",
      tooltip: t("File Issue"),
      onClick: handleCommentClick,
      isVisible: (item, perm) => perm?.CanActive ? true : false, // Show for all items, or add permission check: perm?.CanView
    },
    save: {
      icon: "ri-save-line",
      tooltip: t("Save"),
      onClick: handleSaveCase,
      isVisible: (item, perm) => perm?.CanActive && [1, 4].includes(user.RoleID) ? true : false, 
    },
    reassign: {
      icon: "ri-repeat-line",
      tooltip: t("Re-assign"),
      onClick: handleReassign,
      isVisible: (item, perm) => perm?.CanActive && [1, 4].includes(user.RoleID) ? true : false, 
    },
    // edit: {
    //   icon: "ri-edit-2-line",
    //   tooltip: "Edit",
    //   isVisible: (item, perm) => perm?.CanEdit,
    // },
    delete: {
      icon: "ri-delete-bin-2-line",
      tooltip: t("Delete"),
      onClick: handleDeleteActionClick,
      isVisible: (item, perm) => (user.RoleID == 98 && item.JsonURL) ? false : (user.RoleID == 118 && item.ReviewedByID) ? false : perm?.CanDelete
    },
  };

  const confirmDelete = () => {
    dispatch(deleteCases({ CaseIDs: selectedIds })) 
      .then(() => {
        toast.success(t("Documents deleted successfully"));
        setSelectedIds([]);
      })
      .catch((error) => {
        console.error("Error deleting:", error);
      });
    setDeleteModal(false);
  };

  const saveUserPreferences = async () => {
    const preferencesPayload = {
      ColumnIDs: tableColumnNames.reduce((acc, column) => {
        acc[column.ID] = selectedColumns.includes(column.ID);
        return acc;
      }, {}),
    };

    setModalOpen(false);
    try {
      await dispatch(updateUserPreference(preferencesPayload)).unwrap(); 
      await dispatch(
        fetchCaseTableColumnNames({ 
          module: "CASES", 
        }) 
      );

    } catch (error) {
      console.error("Error updating preferences:", error);
    }
  };

  const handleCaseResponse = (response) => {
    const data = JSON.parse(response) || {};
    if (Array.isArray(data.Data) && data.Data?.length) {
      dispatch(getCases({ Data: data.Data }))
    }
  }

  const Cases = useMemo(() => {
    if (!cases) return [];

    return cases.filter((item) => {
      let isMatch = false;

      switch (activeTab) {
        case "data-entry":
          isMatch = true;
          break;
        case "crawl":
          isMatch = item.Status == 99; // only crawl docs
          break;
        case "bg-process":
          isMatch = user.RoleID == 1 && (item.Status == 3 || item.Status == 10);
          break;
        case "current":
          isMatch = item.Status == 4;
          break;
        case "under-review":
          isMatch = item.Status == 5;
          break;
        case "reviewed":
          isMatch = item.Status == 6;
          break;
        case "legal-issue":
          isMatch = item.Status == 12 || (user.ID == item.ModifiedByID && item.Status == 10);
          break;
        case "document-issue":
          isMatch = item.Status == 13 || (user.ID == item.ModifiedByID && item.Status == 10);
          break;
        default:
          isMatch = true;
      }

      return isMatch;
    });
  }, [cases, activeTab, user.ID]);

  useEffect(() => {
    if (!isConnected) return;

    const payload = {
      authorization: Cookies.get('authorization'),
      DeviceID: Cookies.get('deviceId'),
      langid: localStorage.getItem('langId'),
    }
      
    socket?.emit("GetCase", JSON.stringify(payload));
    socket?.on("CaseResponse", handleCaseResponse)

    return () => {
      socket?.emit("StopCase")
      socket?.off("CaseResponse", handleCaseResponse)
    }

  }, [isConnected])

  useEffect(() => {
    const fetchData = async () => {
      try {
        dispatch(fetchCaseTableColumnNames({ module: "CASES" })); 
        dispatch(fetchAllCases()); 
        dispatch(fetchUsers({ IsSystemUser: true, IsActive: true, RoleID: 118 }));
      } catch (error) {
        console.error(error);
      }
    };

    fetchData();
  }, [dispatch]); 

  return (
    <div className="page-content">
      <Container fluid>
        <Row className="mb-3">
          <Col>
            <BreadCrumb title={t("Cases")} />
          </Col>
        </Row>
        <div className="table-responsive">
          <Card>
            <CardHeader>
              <Row className="g-4">
                <Col className="col-sm-auto">
                  <div>
                    <Button
                      className="btn btn-soft-danger ms-2 me-2"
                      onClick={() => setDeleteModal(true)}
                      disabled={selectedIds.length === 0 || !permission.CanDelete}
                    >
                      <i className="ri-delete-bin-2-line"></i>
                    </Button>
                    { permission.CanAdd &&
                      <Button
                        color="secondary"
                        className="add-btn me-1"
                        onClick={() => navigate("/ai/case-upload")}
                        id="create-btn"
                      >
                        <i className="ri-upload-cloud-2-line align-bottom me-1"></i> {/* Icon for upload */}
                        {t("Upload Case")}
                      </Button>
                    }
                  </div>
                </Col>
                <Col className="col-sm">
                  <div className="d-flex justify-content-sm-end">
                    <Button
                      className="btn btn-soft"
                      onClick={() => setModalOpen(true)}
                      disabled={loading} // Use documentsLoading
                    >
                      <i className="ri-filter-3-line"></i>
                    </Button>
                    <div className="search-box ms-2 me-2">
                      <Input
                        type="text"
                        className="form-control search"
                        placeholder={t("Search")}
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                      />
                      <i className="ri-search-line search-icon"></i>
                    </div>
                  </div>
                </Col>
              </Row>
            </CardHeader>

            <CardBody>
              <div className="listjs-table" id="customerList">
                <Nav tabs style={{ marginBottom: 16 }}>
                  { [1, 98].includes(user.RoleID) && <>
                    <NavItem>
                      <NavLink
                        className={activeTab === "data-entry" ? "active" : ""}
                        onClick={() => handleTabChange("data-entry")}
                        style={{ cursor: "pointer" }}
                      >
                        {t('Data Entry')}
                      </NavLink>
                    </NavItem>
                  </>
                  }
                  { [1, 98].includes(user.RoleID) && <>
                    <NavItem>
                      <NavLink
                        className={activeTab === "crawl" ? "active" : ""}
                        onClick={() => handleTabChange("crawl")}
                        style={{ cursor: "pointer" }}
                      >
                        {t('Crawl')}
                      </NavLink>
                    </NavItem>
                  </>
                  }
                  { [1].includes(user.RoleID) && <>
                    <NavItem>
                      <NavLink
                        className={activeTab === "bg-process" ? "active" : ""}
                        onClick={() => handleTabChange("bg-process")}
                        style={{ cursor: "pointer" }}
                      >
                        {t('BG Process')}
                      </NavLink>
                    </NavItem>
                  </>
                  }
                  { [1, 4, 98].includes(user.RoleID) && <>
                    <NavItem>
                      <NavLink
                        className={activeTab === "current" ? "active" : ""}
                        onClick={() => handleTabChange("current")}
                        style={{ cursor: "pointer" }}
                      >
                        {t('Current Jobs')}
                      </NavLink>
                    </NavItem>
                  </>
                  }
                  { ([1, 4, 118].includes(user.RoleID)) && <>
                    <NavItem>
                      <NavLink
                        className={activeTab === "under-review" ? "active" : ""}
                        onClick={() => handleTabChange("under-review")}
                        style={{ cursor: "pointer" }}
                      >
                        {t('Under Review')}
                      </NavLink>
                    </NavItem>
                  </>
                  }
                  { ([1, 4].includes(user.RoleID)) && <>
                    <NavItem>
                      <NavLink
                        className={activeTab === "reviewed" ? "active" : ""}
                        onClick={() => handleTabChange("reviewed")}
                        style={{ cursor: "pointer" }}
                      >
                        {t('Reviewed')}
                      </NavLink>
                    </NavItem>
                  </>
                  }
                  { ([1, 4, 118].includes(user.RoleID)) && <>
                    <NavItem>
                      <NavLink
                        className={activeTab === "legal-issue" ? "active" : ""}
                        onClick={() => handleTabChange("legal-issue")}
                        style={{ cursor: "pointer" }}
                      >
                        {t('Legal Issue')}
                      </NavLink>
                    </NavItem>
                  </>
                  }
                  { ([1, 4, 118].includes(user.RoleID)) && <>
                    <NavItem>
                      <NavLink
                        className={activeTab === "document-issue" ? "active" : ""}
                        onClick={() => handleTabChange("document-issue")}
                        style={{ cursor: "pointer" }}
                      >
                        {t('Document Issue')}
                      </NavLink>
                    </NavItem>
                  </>
                  }
                </Nav>
              
                { headerLoading ? (
                  <div className="d-flex justify-content-center align-items-center">
                    <Spinner
                      style={{ height: "3rem", width: "3rem" }}
                      className="me-2"
                    >
                      Loading...
                    </Spinner>
                  </div>
                ) : (
                  <div className="listjs-table" id="customerList">
                    <CommonDataTable
                      data={Cases} 
                      tableColumnNames={Array.isArray(tableColumnNames) ? tableColumnNames : []} // Ensure tableColumnNames is an array
                      searchTerm={searchTerm}
                      // onEditClickHandler={onEditClickHandler}
                      // onActiveOrDeactiveChange={onActiveOrDeactiveChange}
                      handleButtonClick={handleButtonClick}
                      setDeleteModal={setDeleteModal}
                      setSelectedIds={setSelectedIds}
                      actionConfig={actionConfig}
                      permission={permission}
                      onPageChange={handlePageChange}
                      totalRecords={Array.isArray(Cases) ? (Cases[0]?.TotalRecords || Cases.length) : 0}
                    />
                  </div>
                )}
              </div>
            </CardBody>
          </Card>
        </div>
      </Container>

      {/* Modal  */}
      {/* <CommonAddModal
        show={show}
        setShow={setShow}
        onSubmitHandler={onSubmitDocDetailsHandler}
        labels={labels}
        isAddOrEdit={isAddOrEdit}
        selectedItemData={selectedRow}
        FormBody={EditDocDetails}
        title={t("EditDocDetails")}
        size="xl"
      /> */}

      {/* ColumnSelectionModal */}
      <ColumnSelectionModal
        isOpen={modalOpen}
        toggle={() => setModalOpen(!modalOpen)}
        columns={Array.isArray(tableColumnNames) ? tableColumnNames : []} // Ensure columns is an array
        selectedColumns={selectedColumns}
        setSelectedColumns={setSelectedColumns}
        handleSave={saveUserPreferences}
      />

      {/* Delete modal  */}
      <DeleteModal
        showDelete={deleteModal}
        setShowDelete={setDeleteModal}
        onDeleteClick={confirmDelete}
        setSelectedIds={setSelectedIds}
        description={t("confirmDelete")}
      />

      {/* Comment Modal  */}
      <FileCommentModal
        title={t("Comment")}
        show={commentModal}
        selectedRow={selectedRow} 
        setShow={setCommentModal}
        onSubmitHandler={handleCommentSubmit}
      />
      
      {/* Assign Modal  */}
      <FileAssignModal
        show={showConfirm}
        doc={selectedRow}
        setShow={setShowConfirm}  
        onReassignClickHandler={onReassignClickHandler}
      />
      
      <MultiFileModal 
        show={showMultiLaw}
        setShow={setShowMultiLaw}
        messages={messages}
        setMessages={setMessages}
      />
      
      <SaveFileModal 
        show={showStatusConfirm}
        setShow={setShowStatusConfirm}
        onSaveClickHandler={onSaveClickHandler}
      />
     
    </div>
  );
}

export default Cases;